import React from "react";

// Reverse geocoding for check-in coordinates (OpenStreetMap Nominatim).
// Nominatim's public API allows ~1 request/second, so requests are queued,
// results are cached (memory + localStorage) and a failed lookup never falls
// back to showing raw coordinates as the location name.

const NOMINATIM_URL = "https://nominatim.openstreetmap.org/reverse";
const REQUEST_GAP_MS = 1100;
const STORAGE_KEY = "geoAddressCache";
const STORAGE_LIMIT = 1000;

const loadStoredCache = () => {
  try {
    return new Map(JSON.parse(localStorage.getItem(STORAGE_KEY)) || []);
  } catch (e) {
    return new Map();
  }
};

const geoCache = loadStoredCache(); // key -> label (successful lookups only)
const geoInFlight = new Map(); // key -> Promise<label | null>
let queueTail = Promise.resolve();

const persistCache = () => {
  try {
    const entries = Array.from(geoCache.entries()).slice(-STORAGE_LIMIT);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch (e) {
    // storage full / unavailable - memory cache still works
  }
};

const toCoord = (value) => {
  const num = parseFloat(value);
  return Number.isFinite(num) ? num : null;
};

// ~11 m precision: nearby check-ins share one lookup
export const getGeoKey = (lat, lng) => {
  const la = toCoord(lat);
  const lo = toCoord(lng);
  if (la === null || lo === null) return null;
  if (la === 0 && lo === 0) return null;
  if (Math.abs(la) > 90 || Math.abs(lo) > 180) return null;
  return `${la.toFixed(4)},${lo.toFixed(4)}`;
};

const buildLabel = (data) => {
  const a = data?.address || {};
  const place =
    a.building ||
    a.office ||
    a.amenity ||
    a.shop ||
    data?.name ||
    (a.house_number && a.road ? `${a.house_number} ${a.road}` : null);
  const street = a.road || a.pedestrian || a.footway;
  const area =
    a.suburb || a.neighbourhood || a.city_district || a.city || a.town || a.village;
  const parts = [place, street !== place ? street : null, area].filter(Boolean);
  return parts.length ? parts.slice(0, 2).join(", ") : null;
};

const requestAddress = (key) => {
  const [lat, lng] = key.split(",");
  const run = () =>
    fetch(
      `${NOMINATIM_URL}?lat=${lat}&lon=${lng}&format=jsonv2&zoom=18&addressdetails=1`,
      { headers: { "Accept-Language": "en" } }
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => (data && !data.error ? buildLabel(data) : null))
      .catch(() => null);

  // Serialize requests with a gap to respect the rate limit
  const result = queueTail.then(run);
  queueTail = result.then(
    () => new Promise((resolve) => setTimeout(resolve, REQUEST_GAP_MS))
  );
  return result;
};

export const fetchAddress = (key) => {
  if (geoCache.has(key)) return Promise.resolve(geoCache.get(key));
  if (geoInFlight.has(key)) return geoInFlight.get(key);

  const promise = requestAddress(key).then((label) => {
    geoInFlight.delete(key);
    if (label) {
      geoCache.set(key, label);
      persistCache();
    }
    return label;
  });
  geoInFlight.set(key, promise);
  return promise;
};

const useGeoAddress = (key) => {
  const [state, setState] = React.useState(() => ({
    address: key ? geoCache.get(key) ?? null : null,
    loading: key ? !geoCache.has(key) : false,
  }));

  React.useEffect(() => {
    if (!key) return;
    if (geoCache.has(key)) {
      setState({ address: geoCache.get(key), loading: false });
      return;
    }
    let cancelled = false;
    setState({ address: null, loading: true });
    fetchAddress(key).then((label) => {
      if (!cancelled) setState({ address: label, loading: false });
    });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return state;
};

const LocationCell = React.memo(({ lat, lng, hasother, locationName }) => {
  const key = getGeoKey(lat, lng);
  // Fixed stations show their configured name; only look up the street /
  // building when the user may check in from anywhere or the name is missing.
  const stationName = !hasother && locationName ? locationName : null;
  const { address, loading } = useGeoAddress(stationName ? null : key);

  if (!key) return <span>-</span>;

  const mapUrl = `https://maps.google.com/maps/?q=${lat},${lng}`;
  const label = stationName || address;

  if (!label) {
    return loading ? (
      <span style={{ color: "#888", fontSize: 12 }}>Resolving…</span>
    ) : (
      <a
        href={mapUrl}
        target="_blank"
        rel="noreferrer"
        title={`${lat}, ${lng}`}
        style={{ color: "#888" }}
      >
        Location unavailable
      </a>
    );
  }

  return (
    <a href={mapUrl} target="_blank" rel="noreferrer" title={`${lat}, ${lng}`}>
      {label}
    </a>
  );
});

export default LocationCell;
